import React from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';

import { Display, Flex, Image, ImageProps } from '@rover/kibble/core';
import type { StringSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import MQ from '@rover/kibble/styles/MediaQuery';
import LandingHero from '@rover/react-lib/src/pages/landing/components/LandingHero';
import LandingSearchForm, {
  Props as LandingSearchFormProps,
} from '@rover/react-lib/src/pages/landing/components/LandingSearchForm';
import { PetType } from '@rover/types/src/Pet';

import {
  boardingCatHeroImage,
  boardingDogHeroImage,
  boardingPetHeroImage,
  doggyDayCareHeroImage,
  dogWalkingHeroImage,
  dropInCatHeroImage,
  dropInDogHeroImage,
  dropInPetHeroImage,
  houseSittingCatHeroImage,
  houseSittingDogHeroImage,
  houseSittingPetHeroImage,
} from '../images';

// Either an explicit `heading` is supplied, or `serviceTypeName` + `locationName` are
// supplied so the component can build the default heading. Requiring one of the two
// prevents rendering an empty heading from missing data.
type HeadingProps =
  | { heading: React.ReactNode | string; serviceTypeName?: string; locationName?: string }
  | { heading?: undefined; serviceTypeName: string; locationName: string };

type Props = {
  desktopImage?: string;
  petType?: string | null;
  internalServiceTypeSlug?: string;
  subheading: string;
  showTrustpilot?: boolean;
} & HeadingProps &
  LandingSearchFormProps;

// Picks the cat/dog/pet-agnostic variant of a service type's art based on pet type. `petType`
// is only ever 'cat' or 'dog' for a real pet-specific page; anything else (unset, a generic
// pet-sitting page with no specific pet type) gets the pet-agnostic variant.
const getPetVariant = (
  petType: string | null | undefined,
  catImage: string,
  dogImage: string,
  petImage: string
): string => {
  if (petType === PetType.CAT) {
    return catImage;
  }
  if (petType === PetType.DOG) {
    return dogImage;
  }
  return petImage;
};

// Selects the hero image based on the pet type and the internal service type, mirroring the
// images the per-service SEO location pages use. Falls back to the boarding image.
export const getHeroImage = (
  petType: string | null | undefined,
  internalServiceTypeSlug?: string
): string => {
  switch (internalServiceTypeSlug) {
    case 'dog-walking':
      return dogWalkingHeroImage;
    case 'doggy-day-care':
      return doggyDayCareHeroImage;
    case 'overnight-traveling':
      return getPetVariant(
        petType,
        houseSittingCatHeroImage,
        houseSittingDogHeroImage,
        houseSittingPetHeroImage
      );
    case 'drop-in':
      return getPetVariant(petType, dropInCatHeroImage, dropInDogHeroImage, dropInPetHeroImage);
    default:
      return getPetVariant(
        petType,
        boardingCatHeroImage,
        boardingDogHeroImage,
        boardingPetHeroImage
      );
  }
};

// 600px is the hero art's native width, so the image renders 1:1 instead of being downscaled
// by a 50% column. Only the widest breakpoint's 1140px Section has room for it alongside the
// 416px text column; below that the two columns stay even halves.
export const HERO_IMAGE_FLEX_BASIS: StringSystemType = ['100%', '50%', '50%', '600px'];

// styled image is required because kibble images don't support aspect-ratio yet
const HeroImage = styled(Image)`
  // we use aspect-ratio instead of using height auto to reduce CLS
  // 1.5 matches the hero art's native 600x400 — keep in sync with the images
  aspect-ratio: 1.5;
  // limit the image height to avoid it being the tallest thing in the Hero
  max-height: 400px;
`;

export const HeroImageWrapper: React.FC<ImageProps> = ({
  description,
  height,
  width,
  src,
  resizeMethod,
  webProps,
}) => {
  return (
    <>
      <Helmet>
        <link
          media={MQ.NOT_XS.getBreakpointString()}
          rel="preload"
          as="image"
          href={src as string}
          fetchPriority="high"
        />
      </Helmet>
      <HeroImage
        description={description}
        height={height}
        width={width}
        src={src}
        resizeMethod={resizeMethod}
        webProps={webProps}
      />
    </>
  );
};

const SeoLocationHero: React.FC<Props> = ({
  desktopImage,
  petType,
  internalServiceTypeSlug,
  serviceTypeName,
  locationName,
  heading,
  subheading,
  showTrustpilot = false,
  ...landingSearchFormProps
}) => {
  const heroImage = desktopImage ?? getHeroImage(petType, internalServiceTypeSlug);

  const heroHeading = heading ?? (
    <>
      {serviceTypeName}{' '}
      <Display webProps={{ as: 'span', responsive: false }} size="500" textColor="display">
        {locationName}
      </Display>
    </>
  );

  return (
    <Flex
      position="relative"
      pt="12x"
      pb="6x"
      justifyContent={['center', 'space-between']}
      alignItems="flex-start"
    >
      <Flex
        width="100%"
        maxWidth="416px"
        justifyContent="center"
        flexDirection="column"
        flexBasis={['100%', '50%', '416px']}
      >
        <LandingHero.Display>{heroHeading}</LandingHero.Display>
        <LandingHero.Text>{subheading}</LandingHero.Text>
        <LandingSearchForm {...landingSearchFormProps} />
        {showTrustpilot && <LandingHero.TrustPilot />}
      </Flex>
      <Flex
        display={['none', 'flex']}
        alignSelf="center"
        alignItems="center"
        justifyContent="center"
        flexBasis={HERO_IMAGE_FLEX_BASIS}
      >
        <HeroImageWrapper
          description=""
          height="auto"
          width="100%"
          src={heroImage}
          resizeMethod="contain"
          webProps={{
            eager: true,
            // @ts-expect-error fetchPriority is not in ImageWebOnlyProps, but is a valid img attribute
            fetchPriority: 'high',
          }}
        />
      </Flex>
    </Flex>
  );
};

export default SeoLocationHero;
